import { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import type { ReactNode } from 'react';
import { olvidarMiniaturas } from '../hooks/useThumbnails';
import { api, ApiError, forgetCsrfToken, rememberCsrfToken } from './api';
import { olvidarLecturas } from './read-cache';
import type { SessionResponse } from './types';

interface SessionState {
  session: SessionResponse | null;
  loading: boolean;
  /**
   * Relee `/api/auth/me` y devuelve lo leído.
   *
   * Devuelve la sesión —y no `void`— porque quien acaba de iniciarla necesita
   * saber si cuajó. Sin valor de retorno, un `refresh()` que responde 401 deja
   * el estado a `null` sin distinguirse de uno que fue bien, la pantalla de
   * entrada navega a la aplicación, la aplicación rebota a la entrada, y el
   * usuario ve un formulario que se limpia sin decir nada.
   */
  refresh: () => Promise<SessionResponse | null>;
  clear: () => void;
}

const SessionContext = createContext<SessionState | null>(null);

/**
 * Estado de sesión de la aplicación.
 *
 * La sesión no se guarda en localStorage: vive en una cookie HttpOnly que el
 * navegador envía sola. Aquí sólo se cachea la respuesta de `/api/auth/me`.
 */
export function SessionProvider({ children }: { children: ReactNode }) {
  const [session, setSession] = useState<SessionResponse | null>(null);
  const [loading, setLoading] = useState(true);

  const refresh = useCallback(async (): Promise<SessionResponse | null> => {
    setLoading(true);
    try {
      const current = await api<SessionResponse>('/api/auth/me');
      // Sin esto, tras recargar la página habría sesión pero no token, y la
      // primera escritura respondería 403.
      rememberCsrfToken(current.csrfToken);
      setSession(current);
      return current;
    } catch (error) {
      // 401 no es un fallo: simplemente no hay sesión iniciada.
      if (!(error instanceof ApiError) || error.status !== 401) {
        console.error('No se pudo cargar la sesión', error);
      }
      forgetCsrfToken();
      setSession(null);
      return null;
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  const value = useMemo<SessionState>(
    () => ({
      session,
      loading,
      refresh,
      clear: () => {
        forgetCsrfToken();
        /*
         * Lo leído y lo firmado pertenecen a quien lo pidió.
         *
         * Las dos cachés viven fuera de React y sobreviven a cerrar sesión, así
         * que en un ordenador compartido la siguiente persona heredaría medio
         * minuto de listados ajenos y unas URLs de fotos que siguen valiendo
         * hasta caducar. Se vacían aquí, que es el único sitio por el que pasan
         * todas las salidas.
         */
        olvidarLecturas();
        olvidarMiniaturas();
        setSession(null);
      },
    }),
    [session, loading, refresh],
  );

  return <SessionContext.Provider value={value}>{children}</SessionContext.Provider>;
}

export function useSession(): SessionState {
  const context = useContext(SessionContext);
  if (!context) throw new Error('useSession debe usarse dentro de <SessionProvider>');
  return context;
}
